import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import { getParties, deleteParty } from "../services/firestore";

import {
  exportPartiesToExcel,
  downloadPartiesTemplate,
  analyzePartiesExcel,
  importPartiesFromExcel
} from "../services/excelService";

import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "../components/ui/alert-dialog";

import {
  Plus,
  Search,
  Building2,
  MapPin,
  FileText,
  MoreVertical,
  Pencil,
  Trash2,
  Upload,
  Download,
  FileSpreadsheet
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../components/ui/dropdown-menu";

import { toast } from "sonner";

export const PartiesPage = () => {

  const { userData, isAdmin } = useAuth();
  const navigate = useNavigate();

  const fileInputRef = useRef(null);

  const [parties, setParties] = useState([]);
  const [filteredParties, setFilteredParties] = useState([]);

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partyToDelete, setPartyToDelete] = useState(null);

  /* IMPORT PREVIEW STATE */

  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importData, setImportData] = useState(null);

  /* =========================
     FETCH PARTIES
  ========================= */

  useEffect(() => {
    fetchParties();
  }, [userData]);

  const fetchParties = async () => {

    if (!userData) return;

    setLoading(true);

    try {

      const data = await getParties();

      setParties(data);
      setFilteredParties(data);

    } catch (error) {

      console.error(error);
      toast.error("Failed to load parties");

    } finally {

      setLoading(false);

    }

  };

  /* =========================
     SEARCH FILTER
  ========================= */

  useEffect(() => {

    if (!searchQuery.trim()) {

      setFilteredParties(parties);
      return;

    }

    const query = searchQuery.toLowerCase();

    const filtered = parties.filter(party =>
      party.name?.toLowerCase().includes(query) ||
      party.gst_number?.toLowerCase().includes(query) ||
      party.address?.toLowerCase().includes(query)
    );

    setFilteredParties(filtered);

  }, [searchQuery, parties]);

  /* =========================
     DELETE PARTY
  ========================= */

  const handleDelete = async () => {

    if (!partyToDelete) return;

    try {

      await deleteParty(partyToDelete.id);

      toast.success("Party deleted successfully");

      fetchParties();

    } catch (error) {

      console.error(error);
      toast.error("Delete failed");

    } finally {

      setDeleteDialogOpen(false);
      setPartyToDelete(null);

    }

  };

  /* =========================
     IMPORT PREVIEW
  ========================= */

  const handleImportFile = async (e) => {

    if (!isAdmin()) {
      toast.error("Only admin can import data");
      return;
    }

    const file = e.target.files[0];

    if (!file) return;

    try {

      const preview = await analyzePartiesExcel(file);

      setImportData(preview);

      setImportPreviewOpen(true);

    } catch (error) {

      console.error(error);
      toast.error("Failed to read Excel");

    }

  };

  /* =========================
     CONFIRM IMPORT
  ========================= */

  const confirmImport = async () => {

    if (!importData) return;

    try {

      toast("Importing parties...");

      await importPartiesFromExcel(importData.rows);

      toast.success("Parties imported successfully");

      fetchParties();

    } catch (error) {

      console.error(error);
      toast.error("Import failed");

    } finally {

      setImportPreviewOpen(false);
      setImportData(null);

    }

  };

  /* =========================
     PARTY CARD
  ========================= */

  const PartyCard = ({ party }) => (

    <Card className="data-card">

      <CardContent className="p-4">

        <div className="flex items-start justify-between">

          <div className="flex items-start gap-3 flex-1 min-w-0">

            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>

            <div className="flex-1 min-w-0">

              <h3 className="font-semibold text-slate-900 truncate">
                {party.name}
              </h3>

              {party.address && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1 truncate">
                  <MapPin size={14} />
                  {party.address}
                </p>
              )}

              {party.gst_number && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  <FileText size={14} />
                  GST: {party.gst_number}
                </p>
              )}

            </div>

          </div>

          <DropdownMenu>

            <DropdownMenuTrigger asChild>

              <Button variant="ghost" size="icon">
                <MoreVertical size={16} />
              </Button>

            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">

              <DropdownMenuItem
                onClick={() => navigate(`/parties/${party.id}/edit`)}
              >
                <Pencil size={14} className="mr-2" />
                Edit
              </DropdownMenuItem>

              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  setPartyToDelete(party);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 size={14} className="mr-2" />
                Delete
              </DropdownMenuItem>

            </DropdownMenuContent>

          </DropdownMenu>

        </div>

        {party.state && (

          <div className="mt-3 pt-3 border-t">

            <span className="text-xs text-slate-500">
              State: {party.state}
            </span>

          </div>

        )}

      </CardContent>

    </Card>

  );

  /* =========================
     LOADING UI
  ========================= */

  if (loading) {

    return (

      <div className="p-4 md:p-6 space-y-4">

        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-full" />

        <div className="grid gap-4">

          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}

        </div>

      </div>

    );

  }

  return (

    <div className="p-4 md:p-6 space-y-4">

      {/* HEADER */}

      <div className="flex items-center justify-between">

        <div>

          <h1 className="text-2xl font-bold">Parties</h1>

          <p className="text-slate-500 text-sm">
            {parties.length} customers
          </p>

        </div>

        <div className="flex gap-2 flex-wrap">

          {isAdmin() && (
            <>
              <Button onClick={downloadPartiesTemplate} variant="outline">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Template
              </Button>

              <Button onClick={exportPartiesToExcel} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>

              <Button
                variant="outline"
                onClick={() => fileInputRef.current.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </>
          )}

          <Button
            className="solar-gradient"
            onClick={() => navigate("/parties/new")}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Party
          </Button>

        </div>

      </div>

      {/* FILE INPUT */}

      <input
        type="file"
        accept=".xlsx,.xls"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleImportFile}
      />

      {/* SEARCH */}

      <div className="relative">

        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />

        <Input
          placeholder="Search by name, GST, or address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
        />

      </div>

      {/* LIST */}

      {filteredParties.length === 0 ? (

        <div className="py-12 text-center">

          <Building2 className="mx-auto mb-3" />

          <p className="text-slate-500">
            {searchQuery ? "No parties match your search" : "No parties added yet"}
          </p>

        </div>

      ) : (

        <div className="grid gap-4">

          {filteredParties.map(party => (
            <PartyCard key={party.id} party={party} />
          ))}

        </div>

      )}

      {/* DELETE DIALOG */}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>

        <AlertDialogContent>

          <AlertDialogHeader>

            <AlertDialogTitle>
              Delete Party?
            </AlertDialogTitle>

            <AlertDialogDescription>
              Are you sure you want to delete "{partyToDelete?.name}"?
            </AlertDialogDescription>

          </AlertDialogHeader>

          <AlertDialogFooter>

            <AlertDialogCancel>Cancel</AlertDialogCancel>

            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>

          </AlertDialogFooter>

        </AlertDialogContent>

      </AlertDialog>

      {/* IMPORT PREVIEW */}

      <AlertDialog
        open={importPreviewOpen}
        onOpenChange={setImportPreviewOpen}
      >

        <AlertDialogContent>

          <AlertDialogHeader>

            <AlertDialogTitle>
              Import Preview
            </AlertDialogTitle>

            <AlertDialogDescription>

              Rows detected: {importData?.total}

              <br />

              Update existing: {importData?.updateCount}

              <br />

              Create new: {importData?.createCount}

            </AlertDialogDescription>

          </AlertDialogHeader>

          <AlertDialogFooter>

            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>

            <AlertDialogAction onClick={confirmImport}>
              Import
            </AlertDialogAction>

          </AlertDialogFooter>

        </AlertDialogContent>

      </AlertDialog>

    </div>

  );

};

export default PartiesPage;